// src/pages/admin/DealersPane.js
import { useEffect, useMemo, useState } from "react";
import { db } from "../../firebaseConfig";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";

const cx = (...xs) => xs.filter(Boolean).join(" ");

// 🔹 Canonical component labels
const COMPONENT_LABELS = {
  PLANTING: "Planting Material",
  MACH_BANK: "Machinery Bank / Tractor",
  IRRIGATION: "Drip / Sprinkler / Talavdi",
  INM_IPM: "INM / IPM / Fertilizer",
  GAP_SOIL: "Soil Testing Lab / GAP",
  PRECISION: "Precision Tech",
  INNOVATIVE: "Innovative Tools",
};

// 🔹 Legacy → canonical
const CANONICAL_CATEGORY_MAP = {
  MACH_BANK: "MACH_BANK",
  MACHINERY: "MACH_BANK",
  IRRIGATION: "IRRIGATION",
  INM_IPM: "INM_IPM",
  GAP_SOIL: "GAP_SOIL",
  PRECISION: "PRECISION",
  INNOVATIVE: "INNOVATIVE",
  TOOLS: "INNOVATIVE",
};
const toComponentId = (raw) => CANONICAL_CATEGORY_MAP[raw] || raw || "";

// ✅ Safe helpers
const safe = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));
const lc = (v) => String(v || "").toLowerCase();
const asArr = (v) => (Array.isArray(v) ? v : []);

// ✅ Vendor safe display name
const vendorName = (v) =>
  v?.company_name ||
  v?.companyName ||
  v?.firmName ||
  v?.firm_name ||
  v?.profile?.companyName ||
  v?.profile?.company_name ||
  v?.profile?.firmName ||
  v?.name ||
  v?.profile?.name ||
  "—";

const StatusPill = ({ status }) => {
  const s = String(status || "pending").toLowerCase();
  const map = {
    approved: "bg-emerald-100 text-emerald-700 border border-emerald-300",
    pending: "bg-amber-50 text-amber-700 border border-amber-300",
    rejected: "bg-rose-50 text-rose-700 border border-rose-300",
  };
  const label = s === "approved" ? "Approved" : s === "rejected" ? "Rejected" : "Pending";
  return (
    <span
      className={cx(
        "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold",
        map[s] || map.pending
      )}
    >
      {label}
    </span>
  );
};

function DealersPane() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  // ✅ Fullscreen vendor modal (already)
  const [open, setOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);

  // vendor products
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  // products filters inside modal
  const [productStatusFilter, setProductStatusFilter] = useState("all");
  const [productCategoryFilter, setProductCategoryFilter] = useState("all");
  const [productSearch, setProductSearch] = useState("");

  // modal tab
  const [activeTab, setActiveTab] = useState("profile"); // profile | products | danger

  // Danger delete confirm
  const [deleteText, setDeleteText] = useState("");

  // action busy
  const [busy, setBusy] = useState(false);

  // ✅ NEW: Dealers list page FULL SCREEN overlay
  const [fullScreen, setFullScreen] = useState(true); // default ON (so it opens like old)

  // ---------------- LOAD VENDORS ----------------
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const snap = await getDocs(collection(db, "farmer_vendors"));
        const list = [];
        snap.forEach((d) => {
          const v = d.data() || {};
          const rawCats = v.componentsOffered || v.categories || [];
          const canonicalCats = Array.from(new Set((rawCats || []).map(toComponentId)));
          list.push({
            id: d.id,
            ...v,
            componentsOffered: canonicalCats,
            categories: canonicalCats,
          });
        });

        list.sort((a, b) => (vendorName(a) || "").localeCompare(vendorName(b) || ""));
        setVendors(list);
      } catch (err) {
        console.error("farmer_vendors load error:", err);
        setError(err.message || "Failed to load vendors.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const summary = useMemo(() => {
    const total = vendors.length;
    const approved = vendors.filter((v) => lc(v.status) === "approved").length;
    const rejected = vendors.filter((v) => lc(v.status) === "rejected").length;
    const pending = total - approved - rejected;
    return { total, approved, pending, rejected };
  }, [vendors]);

  const filteredVendors = useMemo(() => {
    let list = [...vendors];

    if (statusFilter !== "all") {
      list = list.filter((v) => String(v.status || "pending") === statusFilter);
    }

    const needle = search.trim().toLowerCase();
    if (needle) {
      list = list.filter((v) => {
        const s = `${vendorName(v)} ${v.contact_name || ""} ${v.mobile || ""} ${v.email || ""} ${v.district || ""} ${v.taluka || ""}`.toLowerCase();
        return s.includes(needle);
      });
    }

    return list;
  }, [vendors, statusFilter, search]);

  // ---------------- LOAD PRODUCTS for vendor ----------------
  const loadProductsForVendor = async (vendor) => {
    if (!vendor?.id) return;
    setProducts([]);
    setProductsLoading(true);
    try {
      const snap = await getDocs(collection(db, "farmer_vendors", vendor.id, "products"));
      const list = [];
      snap.forEach((d) => {
        const p = d.data() || {};
        const compId = p.componentId || toComponentId(p.category);
        list.push({
          id: d.id,
          ...p,
          componentId: compId,
          category: compId,
        });
      });

      list.sort((a, b) => {
        const aa = a.created_at?.toMillis?.() || a.created_at?.seconds || 0;
        const bb = b.created_at?.toMillis?.() || b.created_at?.seconds || 0;
        return bb - aa;
      });

      setProducts(list);
    } catch (err) {
      console.error("vendor products load error:", err);
      alert("Error loading vendor products: " + err.message);
    } finally {
      setProductsLoading(false);
    }
  };

  const onOpenVendor = async (v) => {
    setSelectedVendor(v);
    setOpen(true);
    setActiveTab("profile");
    setDeleteText("");

    setProductStatusFilter("all");
    setProductCategoryFilter("all");
    setProductSearch("");

    await loadProductsForVendor(v);
  };

  const onCloseVendor = () => {
    setOpen(false);
    setSelectedVendor(null);
    setProducts([]);
    setDeleteText("");
    setBusy(false);
  };

  // ---------------- UPDATE VENDOR STATUS ----------------
  const updateVendorStatus = async (v, status) => {
    if (!v?.id) return;
    try {
      setBusy(true);
      const ref = doc(db, "farmer_vendors", v.id);
      await updateDoc(ref, {
        status,
        updated_at: serverTimestamp(),
      });

      setVendors((prev) => prev.map((x) => (x.id === v.id ? { ...x, status } : x)));

      if (selectedVendor?.id === v.id) {
        setSelectedVendor((p) => ({ ...(p || {}), status }));
      }
    } catch (err) {
      console.error("Update vendor status error:", err);
      alert("Error updating vendor status: " + err.message);
    } finally {
      setBusy(false);
    }
  };

  // ---------------- UPDATE PRODUCT STATUS ----------------
  const updateProductStatus = async (p, status) => {
    if (!selectedVendor?.id || !p?.id) return;
    try {
      setBusy(true);
      const ref = doc(db, "farmer_vendors", selectedVendor.id, "products", p.id);
      await updateDoc(ref, {
        status,
        updated_at: serverTimestamp(),
      });
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, status } : x)));
    } catch (err) {
      console.error("Update product status error:", err);
      alert("Error updating product status: " + err.message);
    } finally {
      setBusy(false);
    }
  };

  // ---------------- DELETE VENDOR (DANGER) ----------------
  const deleteVendor = async () => {
    if (!selectedVendor?.id) return;
    const must = String(vendorName(selectedVendor) || "DELETE").trim();

    if (deleteText.trim() !== must) {
      alert(`Type exactly: ${must}`);
      return;
    }

    const ok = window.confirm(`Delete vendor "${must}" ?\n\nThis will remove vendor profile.`);
    if (!ok) return;

    try {
      setBusy(true);
      await deleteDoc(doc(db, "farmer_vendors", selectedVendor.id));
      setVendors((prev) => prev.filter((x) => x.id !== selectedVendor.id));
      onCloseVendor();
      alert("✅ Vendor deleted.");
    } catch (err) {
      console.error("Delete vendor error:", err);
      alert("Error deleting vendor: " + err.message);
    } finally {
      setBusy(false);
    }
  };

  // ---------------- FILTERED PRODUCTS ----------------
  const filteredProducts = useMemo(() => {
    let list = [...products];

    if (productStatusFilter !== "all") {
      list = list.filter((p) => String(p.status || "pending") === productStatusFilter);
    }
    if (productCategoryFilter !== "all") {
      list = list.filter((p) => (p.componentId || p.category) === productCategoryFilter);
    }

    const needle = productSearch.trim().toLowerCase();
    if (needle) {
      list = list.filter((p) => {
        const s = `${p.title || p.name || ""} ${p.componentId || ""} ${p.companyBrand || ""} ${p.brand || ""} ${p.modelName || ""} ${p.machGroup || ""}`.toLowerCase();
        return s.includes(needle);
      });
    }

    list.sort((a, b) => (a.title || a.name || "").localeCompare(b.title || b.name || ""));
    return list;
  }, [products, productStatusFilter, productCategoryFilter, productSearch]);

  const allProductCategories = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      const id = p.componentId || p.category;
      if (id) set.add(id);
    });
    return Array.from(set);
  }, [products]);

  // ---------------- MAIN UI CONTENT ----------------
  const PageContent = (
    <div className="w-full h-full flex flex-col">
      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <StatCard label="Total Vendors" value={summary.total} tone="emerald" />
        <StatCard label="Approved" value={summary.approved} tone="sky" />
        <StatCard label="Pending" value={summary.pending} tone="amber" />
        <StatCard label="Rejected" value={summary.rejected} tone="rose" />
      </div>

      {/* Header + filters */}
      <div className="mb-4 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2 className="text-lg md:text-xl font-extrabold text-slate-900">Dealers &amp; Vendors</h2>
          <p className="text-xs md:text-sm text-slate-500 mt-0.5">
            Only approved vendors and approved products will be visible to farmers. (District/Taluka locked here.)
          </p>
        </div>

        <div className="flex flex-wrap gap-2 items-center justify-end">
          {!fullScreen ? (
            <button
              type="button"
              onClick={() => setFullScreen(true)}
              className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-[12px] font-extrabold border border-black hover:bg-slate-950"
            >
              ⛶ Open Full Screen
            </button>
          ) : null}

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs md:text-sm border border-slate-400 rounded px-2 py-1 bg-white"
          >
            <option value="all">Status: All</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company / contact / mobile / email / district / taluka"
            className="text-xs md:text-sm border border-slate-400 rounded px-2 py-1 bg-white min-w-[280px]"
          />
        </div>
      </div>

      {error && (
        <div className="mb-3 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3">
          ⚠ Firestore error while loading <b>farmer_vendors</b>:{" "}
          <span className="font-mono">{error}</span>
        </div>
      )}

      {/* Vendors table */}
      <div className="bg-white border border-black rounded-xl overflow-hidden shadow-sm flex-1 min-h-0">
        {loading ? (
          <div className="p-4 text-sm text-slate-500">Loading vendors…</div>
        ) : filteredVendors.length === 0 ? (
          <div className="p-4 text-sm text-slate-500">No farmer vendors registered yet.</div>
        ) : (
          <div className="overflow-auto h-full">
            <table className="min-w-full text-xs md:text-sm border-collapse border border-black">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr>
                  <Th>#</Th>
                  <Th>Company / Address</Th>
                  <Th>Contact</Th>
                  <Th>Mobile</Th>
                  <Th>Area (District / Taluka)</Th>
                  <Th>Components</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filteredVendors.map((v, idx) => {
                  const services = asArr(v.serviceTalukas);
                  const areaLine =
                    safe(v.district) +
                    " / " +
                    (services.length ? `${services.slice(0, 2).join(", ")}${services.length > 2 ? "…" : ""}` : safe(v.taluka));

                  return (
                    <tr
                      key={v.id}
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => onOpenVendor(v)}
                      title="Click to open full details"
                    >
                      <Td>{idx + 1}</Td>

                      <Td>
                        <div className="font-extrabold text-slate-900">{vendorName(v)}</div>
                        <div className="text-[11px] text-slate-600">
                          {String(v.address || "").slice(0, 80)}
                          {String(v.address || "").length > 80 ? "…" : ""}
                        </div>
                      </Td>

                      <Td>
                        <div className="text-slate-900 font-semibold">{safe(v.contact_name)}</div>
                      </Td>

                      <Td className="font-semibold">{safe(v.mobile)}</Td>

                      <Td>
                        <div className="font-semibold text-slate-900">{areaLine}</div>
                        <div className="text-[11px] text-slate-500">Locked</div>
                      </Td>

                      <Td>
                        <div className="flex flex-wrap gap-1">
                          {(v.componentsOffered || v.categories || []).map((id) => (
                            <span
                              key={id}
                              className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-[11px] text-slate-700"
                            >
                              {COMPONENT_LABELS[id] || id}
                            </span>
                          ))}
                        </div>
                      </Td>

                      <Td align="center">
                        <StatusPill status={v.status} />
                      </Td>

                      <Td align="center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-wrap justify-center gap-1">
                          <button
                            onClick={() => updateVendorStatus(v, "approved")}
                            disabled={busy}
                            className="px-2 py-1 rounded bg-emerald-600 text-white text-[11px] border border-black hover:bg-emerald-700 disabled:opacity-60"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => updateVendorStatus(v, "rejected")}
                            disabled={busy}
                            className="px-2 py-1 rounded bg-rose-600 text-white text-[11px] border border-black hover:bg-rose-700 disabled:opacity-60"
                          >
                            Reject
                          </button>
                          <button
                            onClick={() => updateVendorStatus(v, "pending")}
                            disabled={busy}
                            className="px-2 py-1 rounded bg-slate-200 text-slate-800 text-[11px] border border-black hover:bg-slate-300 disabled:opacity-60"
                          >
                            Pending
                          </button>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===================== VENDOR DETAILS MODAL (FULL SCREEN) ===================== */}
      {open && selectedVendor ? (
        <div className="fixed inset-0 z-[99999] bg-black/40">
          <div className="absolute inset-0 p-3 md:p-6">
            <div className="w-full h-full bg-white rounded-2xl border border-black shadow-2xl overflow-hidden flex flex-col">
              {/* Top Bar */}
              <div className="px-4 py-3 bg-slate-900 text-white flex items-center justify-between">
                <div>
                  <div className="text-[12px] font-extrabold">Vendor Details</div>
                  <div className="text-[11px] text-white/80">
                    {vendorName(selectedVendor)} • {safe(selectedVendor.mobile)} • {safe(selectedVendor.email)}
                  </div>
                </div>
                <button
                  onClick={onCloseVendor}
                  className="px-3 py-1.5 rounded-lg border border-white/20 bg-white/10 hover:bg-white/15 text-[12px] font-extrabold"
                >
                  ✕ Close
                </button>
              </div>

              {/* Tabs */}
              <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex gap-2 text-[12px] font-extrabold">
                <TabBtn active={activeTab === "profile"} onClick={() => setActiveTab("profile")}>
                  Profile
                </TabBtn>
                <TabBtn active={activeTab === "products"} onClick={() => setActiveTab("products")}>
                  Products ({products.length})
                </TabBtn>
                <TabBtn active={activeTab === "danger"} onClick={() => setActiveTab("danger")}>
                  Danger Zone
                </TabBtn>

                <div className="ml-auto flex gap-2">
                  <span className="px-2 py-1 rounded-full bg-white border border-black text-[11px]">
                    Status: <b>{safe(selectedVendor.status || "pending")}</b>
                  </span>
                  <button
                    onClick={() => updateVendorStatus(selectedVendor, "approved")}
                    disabled={busy}
                    className="px-3 py-1 rounded-lg bg-emerald-600 text-white border border-black text-[11px] hover:bg-emerald-700 disabled:opacity-60"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => updateVendorStatus(selectedVendor, "rejected")}
                    disabled={busy}
                    className="px-3 py-1 rounded-lg bg-rose-600 text-white border border-black text-[11px] hover:bg-rose-700 disabled:opacity-60"
                  >
                    Reject
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-auto p-4">
                {/* PROFILE */}
                {activeTab === "profile" && (
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <div className="lg:col-span-7 space-y-3">
                      <Card title="Company / Contact">
                        <Grid2 a="Company" b={vendorName(selectedVendor)} c="Contact" d={safe(selectedVendor.contact_name)} />
                        <Grid2 a="Mobile" b={safe(selectedVendor.mobile)} c="Email" d={safe(selectedVendor.email)} />
                        <Grid1 a="Address" b={safe(selectedVendor.address)} />
                      </Card>

                      <Card title="Components / Brands">
                        <Grid1
                          a="Components Offered"
                          b={
                            (selectedVendor.componentsOffered || selectedVendor.categories || [])
                              .map((id) => COMPONENT_LABELS[id] || id)
                              .join(", ") || "—"
                          }
                        />
                        <Grid1
                          a="Tractor Brands"
                          b={asArr(selectedVendor.tractorBrands).length ? asArr(selectedVendor.tractorBrands).join(", ") : "—"}
                        />
                      </Card>
                    </div>

                    <div className="lg:col-span-5 space-y-3">
                      <Card title="Location (Locked)">
                        <Grid2 a="State" b={safe(selectedVendor.state)} c="District" d={safe(selectedVendor.district)} />
                        <Grid1
                          a="Taluka"
                          b={
                            asArr(selectedVendor.serviceTalukas).length
                              ? asArr(selectedVendor.serviceTalukas).join(", ")
                              : safe(selectedVendor.taluka)
                          }
                        />
                        <div className="mt-2 text-[11px] text-slate-600">
                          ✅ District/Taluka are locked here.
                        </div>
                      </Card>

                      <Card title="Quick Stats">
                        <div className="grid grid-cols-3 gap-2">
                          <MiniStat label="Products" value={products.length} />
                          <MiniStat label="Approved" value={products.filter((p) => lc(p.status) === "approved").length} />
                          <MiniStat label="Pending" value={products.filter((p) => lc(p.status) !== "approved" && lc(p.status) !== "rejected").length} />
                        </div>
                      </Card>
                    </div>
                  </div>
                )}

                {/* PRODUCTS */}
                {activeTab === "products" && (
                  <div className="space-y-3">
                    <div className="flex flex-col md:flex-row md:items-center gap-2 justify-between">
                      <div className="text-[12px] font-extrabold text-slate-900">
                        Products for: {vendorName(selectedVendor)}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <select
                          value={productStatusFilter}
                          onChange={(e) => setProductStatusFilter(e.target.value)}
                          className="text-[12px] border border-black rounded px-2 py-1 bg-white"
                        >
                          <option value="all">Status: All</option>
                          <option value="approved">Approved</option>
                          <option value="pending">Pending</option>
                          <option value="rejected">Rejected</option>
                        </select>

                        <select
                          value={productCategoryFilter}
                          onChange={(e) => setProductCategoryFilter(e.target.value)}
                          className="text-[12px] border border-black rounded px-2 py-1 bg-white"
                        >
                          <option value="all">Component: All</option>
                          {allProductCategories.map((id) => (
                            <option key={id} value={id}>
                              {COMPONENT_LABELS[id] || id}
                            </option>
                          ))}
                        </select>

                        <input
                          value={productSearch}
                          onChange={(e) => setProductSearch(e.target.value)}
                          placeholder="Search product / brand / model / group"
                          className="text-[12px] border border-black rounded px-2 py-1 bg-white min-w-[240px]"
                        />
                      </div>
                    </div>

                    <div className="border-2 border-black rounded-xl overflow-hidden">
                      {productsLoading ? (
                        <div className="p-4 text-sm text-slate-500">Loading products…</div>
                      ) : filteredProducts.length === 0 ? (
                        <div className="p-4 text-sm text-slate-500">No products for selected filters.</div>
                      ) : (
                        <div className="overflow-auto max-h-[520px] bg-white">
                          <table className="min-w-full text-[12px] border-collapse border border-black">
                            <thead className="bg-slate-100 sticky top-0 z-10">
                              <tr>
                                <Th>#</Th>
                                <Th>Title / Details</Th>
                                <Th>Component</Th>
                                <Th>Status</Th>
                                <Th>Actions</Th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredProducts.map((p, idx) => {
                                const compId = p.componentId || p.category;
                                const st = String(p.status || "pending");

                                return (
                                  <tr key={p.id} className="hover:bg-slate-50">
                                    <Td>{idx + 1}</Td>

                                    <Td>
                                      <div className="font-extrabold text-slate-900">{p.title || p.name || "—"}</div>
                                      {compId === "MACH_BANK" ? (
                                        <div className="text-[11px] text-slate-600">
                                          Brand: <b>{p.companyBrand || p.brand || "—"}</b> • Model:{" "}
                                          <b>{p.modelName || "—"}</b> {p.hpRating ? `• HP: ${p.hpRating}` : ""}
                                          {p.machGroup ? ` • Group: ${p.machGroup}` : ""}
                                        </div>
                                      ) : (
                                        <div className="text-[11px] text-slate-600">
                                          Unit: <b>{p.unitType || p.unit || "—"}</b> • Price:{" "}
                                          <b>{p.basePrice || p.price ? `₹${p.basePrice || p.price}` : "—"}</b>
                                        </div>
                                      )}
                                    </Td>

                                    <Td className="text-center">{COMPONENT_LABELS[compId] || compId}</Td>

                                    <Td className="text-center">
                                      <StatusPill status={st} />
                                    </Td>

                                    <Td className="text-center">
                                      <div className="flex justify-center flex-wrap gap-1">
                                        <button
                                          disabled={busy}
                                          onClick={() => updateProductStatus(p, "approved")}
                                          className="px-2 py-1 rounded bg-emerald-600 text-white text-[11px] border border-black hover:bg-emerald-700 disabled:opacity-60"
                                        >
                                          Approve
                                        </button>
                                        <button
                                          disabled={busy}
                                          onClick={() => updateProductStatus(p, "rejected")}
                                          className="px-2 py-1 rounded bg-rose-600 text-white text-[11px] border border-black hover:bg-rose-700 disabled:opacity-60"
                                        >
                                          Reject
                                        </button>
                                        <button
                                          disabled={busy}
                                          onClick={() => updateProductStatus(p, "pending")}
                                          className="px-2 py-1 rounded bg-slate-200 text-slate-800 text-[11px] border border-black hover:bg-slate-300 disabled:opacity-60"
                                        >
                                          Pending
                                        </button>
                                      </div>
                                    </Td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* DANGER */}
                {activeTab === "danger" && (
                  <div className="max-w-3xl">
                    <div className="rounded-2xl border border-rose-300 bg-rose-50 p-4">
                      <div className="text-[13px] font-extrabold text-rose-800">Danger Zone</div>
                      <div className="text-[12px] text-rose-700 mt-1">
                        Delete vendor profile permanently.
                      </div>

                      <div className="mt-3 text-[12px] text-slate-800">
                        Type vendor company name to confirm:
                      </div>

                      <div className="mt-2 flex flex-col sm:flex-row gap-2">
                        <input
                          value={deleteText}
                          onChange={(e) => setDeleteText(e.target.value)}
                          className="flex-1 border border-black rounded px-3 py-2 text-[12px] bg-white"
                          placeholder={vendorName(selectedVendor)}
                        />
                        <button
                          disabled={busy}
                          onClick={deleteVendor}
                          className="px-4 py-2 rounded bg-rose-700 text-white font-extrabold border border-black hover:bg-rose-800 disabled:opacity-60"
                        >
                          🗑 Delete Vendor
                        </button>
                      </div>

                      <div className="mt-3 text-[11px] text-rose-700">
                        NOTE: This page does NOT allow editing District/Taluka.
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-4 py-3 border-t border-slate-200 bg-white flex items-center justify-between">
                <div className="text-[11px] text-slate-600">
                  Tip: Vendor approval + Product approval दोनों जरूरी हैं ताकि farmer portal पर दिखे.
                </div>
                <button
                  onClick={onCloseVendor}
                  className="px-4 py-2 rounded-xl border border-black bg-slate-100 hover:bg-slate-200 text-[12px] font-extrabold"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

  // ✅ FULL SCREEN LIST OVERLAY
  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-[99998] bg-white">
        <div className="h-full w-full flex flex-col">
          <div className="px-4 py-3 bg-slate-950 text-white flex items-center justify-between border-b border-black">
            <div>
              <div className="text-[12px] font-extrabold">Dealers & Vendors — Full Screen</div>
              <div className="text-[11px] text-white/80">This is the full-screen list window (like old).</div>
            </div>
            <button
              onClick={() => setFullScreen(false)}
              className="px-3 py-1.5 rounded-lg border border-white/20 bg-white/10 hover:bg-white/15 text-[12px] font-extrabold"
            >
              ✕ Close Full Screen
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden p-4">{PageContent}</div>
        </div>
      </div>
    );
  }

  // Normal embedded view
  return <div className="w-full">{PageContent}</div>;
}

/* ---------- small helpers ---------- */
function Th({ children }) {
  return (
    <th className="px-2 py-2 text-[11px] font-extrabold text-slate-700 border border-black text-left">
      {children}
    </th>
  );
}

function Td({ children }) {
  return (
    <td className="px-2 py-2 text-[12px] text-slate-800 border border-black align-top">
      {children}
    </td>
  );
}

function StatCard({ label, value, tone }) {
  const map = {
    emerald: "from-emerald-500 to-emerald-600",
    sky: "from-sky-500 to-sky-600",
    amber: "from-amber-500 to-amber-600",
    rose: "from-rose-500 to-rose-600",
  };
  const gradient = map[tone] || map.emerald;
  return (
    <div className="bg-white border border-slate-300 rounded-xl shadow-sm overflow-hidden">
      <div className={cx("px-3 py-1.5 text-[10px] font-extrabold text-white bg-gradient-to-r", gradient)}>
        {label}
      </div>
      <div className="px-3 py-3">
        <div className="text-xl font-black text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={cx(
        "px-3 py-1.5 rounded-full border text-[11px] font-extrabold",
        active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"
      )}
    >
      {children}
    </button>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-[12px] font-extrabold text-slate-900 mb-2">{title}</div>
      {children}
    </div>
  );
}

function Grid2({ a, b, c, d }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <div className="text-[10px] text-slate-500 font-extrabold uppercase">{a}</div>
        <div className="text-[12px] text-slate-900 font-extrabold mt-1">{b}</div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <div className="text-[10px] text-slate-500 font-extrabold uppercase">{c}</div>
        <div className="text-[12px] text-slate-900 font-extrabold mt-1">{d}</div>
      </div>
    </div>
  );
}

function Grid1({ a, b }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      <div className="text-[10px] text-slate-500 font-extrabold uppercase">{a}</div>
      <div className="text-[12px] text-slate-900 font-extrabold mt-1 break-words">{b}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      <div className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500">{label}</div>
      <div className="mt-1 text-[14px] font-black text-slate-900">{value}</div>
    </div>
  );
}

export default DealersPane;
